import React from 'react';
import { useModuleAccess } from 'hooks/useModuleAccess';
import type { ModuleType } from 'models/shop/moduleTypes';
import { observer } from 'mobx-react-lite';

interface ModuleGuardProps {
  /**
   * Модуль или массив модулей, которые должны быть доступны
   */
  module: ModuleType | ModuleType[];
  /**
   * Режим проверки для массива модулей:
   * - 'all' - все модули должны быть доступны (по умолчанию)
   * - 'any' - хотя бы один модуль должен быть доступен
   */
  mode?: 'all' | 'any';
  /**
   * Контент, который отображается, если модуль доступен
   */
  children: React.ReactNode;
  /**
   * Компонент, который отображается, если модуль недоступен
   */
  fallback?: React.ReactNode;
}

/**
 * Компонент для условного рендеринга контента в зависимости от доступности модулей
 *
 * @param root0
 * @param root0.module
 * @param root0.mode
 * @param root0.children
 * @param root0.fallback
 */
export const ModuleGuard: React.FC<ModuleGuardProps> = observer(
  ({ module, mode = 'all', children, fallback = null }) => {
    const { isModuleEnabled, areModulesEnabled, isAnyModuleEnabled } = useModuleAccess();

    const isAccessible = React.useMemo(() => {
      if (Array.isArray(module)) {
        return mode === 'all' ? areModulesEnabled(module) : isAnyModuleEnabled(module);
      }

      return isModuleEnabled(module);
    }, [module, mode, isModuleEnabled, areModulesEnabled, isAnyModuleEnabled]);

    return isAccessible ? <>{children}</> : <>{fallback}</>;
  }
);
